import { useEffect, useMemo, useReducer, useState } from 'react';
import { DialFace } from '../dial/Dial';
import { HoldButton } from '../ui/HoldButton';
import { ScoreList, revealSentence, type RevealRow } from '../ui/reveal';
import { useAnnounce, useFocusOnChange, useSweep } from '../ui/shell';
import { emptyLocalState, localReducer, standings, winners } from './engine';
import { CLUE_MAX } from '@shared/protocol';
import type { DeckId } from '@shared/content';
import { dial10, fmtScore, initialsFor } from '@shared/scoring';

/**
 * One phone, passed around. The v1 experience, unchanged apart from the fixes
 * the Gate A panel asked for.
 */
export function LocalGame({
  names,
  passes,
  deck,
  onExit,
}: {
  names: string[];
  passes: 1 | 2;
  deck: DeckId;
  onExit: () => void;
}) {
  const [state, dispatch] = useReducer(localReducer, undefined, emptyLocalState);
  const announce = useAnnounce();
  const started = state.players.length > 0;

  useEffect(() => {
    if (!started) dispatch({ t: 'start', names, passes, deck });
  }, [started, names, passes, deck]);

  const headingRef = useFocusOnChange<HTMLHeadingElement>(`${state.screen}:${state.round}:${state.queuePos}`);
  const roundLabel = `Round ${state.round + 1} of ${state.totalRounds}`;

  if (!started) return null;

  const giver = state.players[state.giverIdx];

  switch (state.screen) {
    case 'handoff': {
      const to = state.players[state.handoff!.toIdx];
      return (
        <section className="screen">
          <div className="bar">
            <span className="eyebrow">{roundLabel}</span>
            <span className="eyebrow">Pass the phone</span>
          </div>
          <div className="body-area handoff">
            <span className="eyebrow">Hand to</span>
            <h1 className="handoff-name" tabIndex={-1} ref={headingRef}>
              {to.name}
            </h1>
            <p className="handoff-sub" id="handoff-sub">
              {state.handoff!.sub}
            </p>
            <HoldButton label="Hold to reveal" onComplete={() => dispatch({ t: 'handoffDone' })} describedBy="handoff-sub" />
          </div>
        </section>
      );
    }

    case 'clue':
      return (
        <ClueScreen
          roundLabel={roundLabel}
          giverName={giver.name}
          card={state.card!}
          target={state.target}
          headingRef={headingRef}
          onSubmit={(text) => {
            announce('Clue locked in. Pass the phone.');
            dispatch({ t: 'submitClue', text });
          }}
        />
      );

    case 'guess': {
      const who = state.players[state.queue[state.queuePos]];
      return (
        <GuessScreen
          roundLabel={roundLabel}
          who={who.name}
          clue={state.clue}
          card={state.card!}
          headingRef={headingRef}
          onSubmit={(value) => dispatch({ t: 'submitGuess', value })}
        />
      );
    }

    case 'reveal': {
      const rows: RevealRow[] = state.guesses.map((g) => ({
        name: state.players[g.playerIdx].name,
        value: g.value,
        points: g.points,
        total: state.players[g.playerIdx].score,
      }));
      return (
        <RevealScreen
          roundLabel={roundLabel}
          card={state.card!}
          target={state.target}
          clue={state.clue}
          giverName={giver.name}
          giverPoints={state.giverPoints}
          giverTotal={giver.score}
          rows={rows}
          markers={(() => {
            // Disambiguated across the whole room, not name by name: two "EM"
            // pills on one dial is a reveal that lies.
            const labels = initialsFor(state.players.map((p) => p.name));
            return state.guesses.map((g) => ({ value: g.value, label: labels[g.playerIdx] }));
          })()}
          headingRef={headingRef}
          isLast={state.round + 1 >= state.totalRounds}
          coop={
            state.players.length === 2
              ? { combined: state.players.reduce((a, p) => a + p.score, 0), possible: state.scoredRounds * 8 }
              : null
          }
          onNext={() => dispatch({ t: 'nextRound' })}
          onEnd={() => dispatch({ t: 'endGame' })}
        />
      );
    }

    case 'final': {
      const ranked = standings(state.players);
      const won = winners(state.players);
      const coop = state.players.length === 2;
      const possible = state.scoredRounds * 8;
      const combined = state.players.reduce((a, p) => a + p.score, 0);
      return (
        <section className="screen">
          <div className="bar">
            <span className="eyebrow">Final</span>
            <span className="eyebrow">
              {state.totalRounds} round{state.totalRounds === 1 ? '' : 's'}
            </span>
          </div>
          <div className="body-area">
            <div>
              <h1 className="final-h" tabIndex={-1} ref={headingRef}>
                {coop
                  ? `${fmtScore(combined)} of a possible ${possible}`
                  : won.length > 1
                    ? `${won.join(' & ')} tie`
                    : `${won[0]} wins`}
              </h1>
              <p className="final-sub">
                {coop
                  ? 'Two players score identically every round, so this one is co-op. See how high you can push it.'
                  : `${fmtScore(Math.max(...state.players.map((p) => p.score)))} points`}
              </p>
            </div>
            <ul className="scores">
              {ranked.map(({ player }, i) => (
                <li key={player.name} className={!coop && i === 0 ? 'win' : undefined}>
                  <span className="tag">{coop ? '—' : `#${i + 1}`}</span>
                  <span className="who">{player.name}</span>
                  <span className="pts">{fmtScore(player.score)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="actions">
            <button type="button" className="btn" onClick={() => dispatch({ t: 'playAgain' })}>
              Play again
            </button>
            <button type="button" className="btn ghost" onClick={onExit}>
              Change players
            </button>
          </div>
        </section>
      );
    }

    default:
      return null;
  }
}

function ClueScreen({
  roundLabel,
  giverName,
  card,
  target,
  headingRef,
  onSubmit,
}: {
  roundLabel: string;
  giverName: string;
  card: [string, string];
  target: number;
  headingRef: React.RefCallback<HTMLHeadingElement>;
  onSubmit: (text: string) => void;
}) {
  const [text, setText] = useState('');
  const clean = text.trim();
  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        <span className="eyebrow hot">{giverName} writes the clue</span>
      </div>
      <div className="body-area">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Write your clue
        </h1>
        <p className="readout">
          <small>The hidden target — only you see this</small>
          {dial10(target)}
        </p>
        <DialFace value={target} poles={card} label="The hidden target" />
        <div>
          <label className="visually-hidden" htmlFor="clue-input">
            Your clue
          </label>
          <textarea
            id="clue-input"
            maxLength={CLUE_MAX}
            rows={2}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Write one clue that lands exactly on the target…"
          />
          <p className="counter">
            {text.length} / {CLUE_MAX}
          </p>
        </div>
      </div>
      <div className="actions">
        <button type="button" className="btn" disabled={!clean} onClick={() => onSubmit(clean)}>
          Lock in clue
        </button>
      </div>
    </section>
  );
}

function GuessScreen({
  roundLabel,
  who,
  clue,
  card,
  headingRef,
  onSubmit,
}: {
  roundLabel: string;
  who: string;
  clue: string;
  card: [string, string];
  headingRef: React.RefCallback<HTMLHeadingElement>;
  onSubmit: (value: number) => void;
}) {
  const [value, setValue] = useState(50);
  // The needle starts at 5.0, so a live button can't tell "I considered it and
  // 5.0 is right" from "I wasn't playing" — and one checked-out player locking
  // 5.0 every round drags the giver's average and pollutes the reveal.
  const [touched, setTouched] = useState(false);
  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        <span className="eyebrow hot">{who} guesses</span>
      </div>
      <div className="body-area">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Your guess
        </h1>
        <div className="cluecard">
          <span className="eyebrow">The clue</span>
          <p>{clue}</p>
        </div>
        <p className="readout">
          <small>Your guess</small>
          {dial10(value)}
        </p>
        <DialFace
          value={value}
          poles={card}
          onChange={(v) => {
            setValue(v);
            setTouched(true);
          }}
        />
        <p className="hint">Drag the needle</p>
      </div>
      <div className="actions">
        <button type="button" className="btn" disabled={!touched} onClick={() => onSubmit(value)}>
          {touched ? 'Lock in guess' : 'Move the needle first'}
        </button>
      </div>
    </section>
  );
}

export function RevealScreen({
  roundLabel,
  card,
  target,
  clue,
  giverName,
  giverPoints,
  giverTotal,
  rows,
  markers,
  headingRef,
  isLast,
  onNext,
  onEnd,
  advanceLabel,
  waitingFor,
  coop,
}: {
  roundLabel: string;
  card: [string, string];
  target: number;
  clue: string;
  giverName: string;
  giverPoints: number;
  giverTotal: number;
  rows: RevealRow[];
  markers: { value: number; label: string }[];
  headingRef: React.RefCallback<HTMLHeadingElement>;
  isLast: boolean;
  onNext?: () => void;
  onEnd?: () => void;
  advanceLabel?: string;
  waitingFor?: string | null;
  /** Two-player games score identically every round, so they read as co-op. */
  coop?: { combined: number; possible: number } | null;
}) {
  const [landed, setLanded] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const announce = useAnnounce();
  const swept = useSweep(target, 50, () => setLanded(true));
  const sentence = useMemo(
    () => revealSentence(giverName, giverPoints, rows, target),
    [giverName, giverPoints, rows, target],
  );

  useEffect(() => {
    if (landed) announce(`Reveal. Target ${dial10(target)}. ${sentence}`);
  }, [landed, announce, sentence, target]);

  return (
    <section className="screen">
      <div className="bar">
        <span className="eyebrow">{roundLabel}</span>
        {coop && <span className="eyebrow hot">{fmtScore(coop.combined)} of {coop.possible}</span>}
        {onEnd ? (
          <button type="button" className="eyebrow linkish" onClick={() => setConfirmEnd(true)}>
            End game early
          </button>
        ) : (
          <span className="eyebrow">Reveal</span>
        )}
      </div>
      <div className="body-area top">
        <h1 className="visually-hidden" tabIndex={-1} ref={headingRef}>
          Reveal
        </h1>
        <p className="hint">{landed ? `Target ${dial10(target)}` : 'Finding the target…'}</p>
        <DialFace
          value={swept}
          poles={card}
          target={landed ? target : null}
          markers={landed ? markers : []}
          label="The target"
        />
        {/* Held back until the needle lands. Stating "Bo was 2.5 away" while
            the target is still hidden gives the target away, and it steps on
            the one orchestrated moment in the app. */}
        {landed && (
          <>
            <p className="waiting-sub">{sentence}</p>
            <ScoreList
              giverName={giverName}
              giverClue={clue}
              giverPoints={giverPoints}
              giverTotal={giverTotal}
              rows={rows}
            />
          </>
        )}
      </div>
      <div className="actions">
        {confirmEnd ? (
          <>
            <p className="note">End the game now? Scores stand.</p>
            <button type="button" className="btn danger" onClick={onEnd}>
              End game
            </button>
            <button type="button" className="btn ghost" onClick={() => setConfirmEnd(false)}>
              Keep playing
            </button>
          </>
        ) : onNext ? (
          <button type="button" className="btn" onClick={onNext}>
            {advanceLabel ?? (isLast ? 'See final scores' : 'Next round')}
          </button>
        ) : (
          <p className="hint">{waitingFor ? `Waiting for ${waitingFor}` : ''}</p>
        )}
      </div>
    </section>
  );
}
